/* eslint-disable prettier/prettier */
import regexes from "shared/imports/regex"

import {
    defaultSafedSearchObj,
    defaultSearchifyObj,
    DISCLAIMER_REGEX,
    MAX_CHARS_PER_TEXT,
} from "app/static/frontend/outbound-texting/constants"

// returns a placeholder for a campaign link to the given campaign
export const getCampaignLinkPlaceholder = (campaignId) => `{{campaign_link__${campaignId}}}`

/**
 * Converts two numbers representing a proportion into a rounded
 * integer representing a percentage (out of 100).
 * If the proportion would be NaN, returns 0.
 * If the proportion would be Infinity, uses 1 as the denominator.
 *
 * @name proportionToIntegerPercent
 * @function
 * @param {Number} numerator - part out of the whole value
 * @param {Number} denominator - the whole value
 *
 * @returns {Number} A whole number where 100 represents the whole (i.e. percentage points)
 */
export const proportionToIntegerPercent = (numerator, denominator) =>
    Math.round((numerator / (denominator || 1)) * 100) || 0

/**
 * Given the length of a string (in number of characters), returns a string
 * to show as the character counter under the text area; shows the number of
 * SMS messages required to convey the entire message to a single recipient.
 *
 * @name numMessagesCounterFormatter
 * @function
 * @param {Number} length   - number of characters in the text
 * @param {Boolean} isApproximate   - true if the count is approximate, rather than exact
 *
 * @returns {String}    The formatted text length counter
 */
export const numMessagesCounterFormatter = (length, isApproximate) => {
    const minNumMessages = Math.ceil(length / MAX_CHARS_PER_TEXT)
    const nextMessageThreshold = MAX_CHARS_PER_TEXT * minNumMessages
    const approx = isApproximate ? "~" : ""
    const pluralized = minNumMessages === 1 ? "message" : "messages"
    return `${approx}${length} / ${nextMessageThreshold}\t(${approx}${minNumMessages} ${pluralized})`
}

/**
 * Convert safed search filter to searchify filter format
 * read more at https://quorumanalytics.atlassian.net/l/cp/wbdopnfs
 */
export const toSearchifyFormat = (safedSearchObj) => {
    const { added_ids, deleted_ids, filters, estimated_count, ...rest } = safedSearchObj
    return {
        ...defaultSearchifyObj,
        addedIds: added_ids || defaultSearchifyObj.addedIds,
        deletedIds: deleted_ids || defaultSearchifyObj.deletedIds,
        querysetList: filters || defaultSearchifyObj.querysetList,
        searchifyCount: estimated_count || defaultSearchifyObj.searchifyCount,
        ...rest,
    }
}

/**
 * Convert searchify filter to safed search filter format
 * read more https://quorumanalytics.atlassian.net/l/cp/wbdopnfs
 */
export const toSafedSearchFormat = (searchifyObj) => {
    const { addedIds, deletedIds, querysetList, searchifyCount, ...rest } = searchifyObj
    return {
        ...defaultSafedSearchObj,
        added_ids: addedIds || defaultSafedSearchObj.added_ids,
        deleted_ids: deletedIds || defaultSafedSearchObj.deleted_ids,
        filters: querysetList || defaultSafedSearchObj.filters,
        estimated_count: searchifyCount || searchifyObj.estimated_count || defaultSafedSearchObj.estimated_count,
        ...rest,
    }
}

/**
 *
 * WARNING! This function don't implement correctly the safed search or searchify filter objects
 *          specifications as in https://quorumanalytics.atlassian.net/l/cp/wbdopnfs
 *          For the correct implementation, use the functions toSearchifyFormat and toSafedSearchFormat
 *
 * Given an object representing a searchify saved search, returns a new
 * object with keys and values transposed into a form that the API can
 * use to create a SafedSearch.
 *
 * Mostly necessary to move the "filters" on to the "advSearchParams" key
 * and also removes extra keys used on the frontend only in the process
 *
 * @name searchifyToSafedSearchFormat
 * @function
 * @param {searchifyObj} Object   - an object holding the saved search as created
 *                                  by searchify
 *
 * @returns {Object}    A new object with keys in the format that the API can use
 *                      to create a SafedSearch
 */
export const searchifyToSafedSearchFormat = (searchifyObj) => ({
    added_ids: searchifyObj.added_ids,
    deleted_ids: searchifyObj.deleted_ids,
    advSearchParams: searchifyObj.filters,
    quorumDataType: searchifyObj.data_type,
    user: searchifyObj.user,
})

/**
 * Given an object representing a backend saved search, returns a new
 * object with keys and values transposed into a form that SearchifyField
 * can use to load existing SafedSearches into searchify
 *
 * Mostly necessary to switch the "advSearchParams" on to the "filters" key
 * Essentially the reverse of searchifyToSafedSearchFormat
 *
 * @name safedSearchToSearchifyFormat
 * @function
 * @param {safedSearchObj} Object   - an object holding the SafedSearch as retrieved
 *                                  from the backend
 *
 * @returns {Object}    A new object with keys in the format that SearchifyField can
 *                      use as its underlying value
 */
export const safedSearchToSearchifyFormat = (safedSearchObj) => ({
    added_ids: safedSearchObj.added_ids || [],
    deleted_ids: safedSearchObj.deleted_ids || [],
    filters: safedSearchObj.advSearchParams || safedSearchObj.filters || [],
    data_type: safedSearchObj.quorumDataType,
    user: safedSearchObj.user,
})

/**
 * Given a comma separated list of phone numbers as a string, returns an object
 * with an array of valid phone numbers computed from the input strings and an
 * array of invalid phone numbers. Valid numbers are determined by trying to clean
 * the input string, splitting on the commas, and validating each number for length.
 *
 * @name parseTestNumberInput
 * @function
 * @param {testNumbers} String  - a string containing a comma-serparated list
 *                                  of phone numbers
 *
 * @returns {Object}    An object of the shape:
 *      hasValidNumbers:    true if the input contained any valid numbers
 *      validNumbers:       an array of valid numbers from the input after cleaning
 *      invalidNumbers:     an array of invalid numbers from the input after cleaning
 */
export const parseTestNumberInput = (testNumbers) => {
    const cleanInput = testNumbers
        .replace(regexes.removeEndCommaAndSpaces, "")
        .replace(regexes.phoneNumberReplacerRegex, "")

    const invalidNumbers = []
    const validNumbers = []
    cleanInput
        .replace(/\s/g, "")
        .split(",")
        .forEach((number) => {
            regexes.phoneNumberValidationRegex.test(number) ? validNumbers.push(number) : invalidNumbers.push(number)
        })

    return {
        hasValidNumbers: Boolean(validNumbers.length),
        invalidNumbers,
        validNumbers,
    }
}

/**
 * Given a string, returns true if the default outbound texting disclaimer is found in the string.
 *
 * @name containsDisclaimerString
 * @function
 * @param {message} String  - text to check
 *
 * @returns {Boolean}   true if the string contains the disclaimer text exactly, false otherwise
 */
export const containsDisclaimerString = (message) => {
    if (typeof message !== "string") {
        throw new Error("Message to validate must be a string")
    }

    return Boolean(message && message.match(DISCLAIMER_REGEX))
}

/**
 * Given a string - a custom subscription confirmation message - determines if
 * the custom message meets our requirements. These requirements are:
 * must be shorter than the amount of characters a single text message can carry
 * must contain the default disclaimer text
 *
 * @name validateCustomSubscriptionConfirmationMessage
 * @function
 * @param {testNumbers} String  - text to check
 *
 * @returns {Object}    An object of the shape:
 *      isValid:            true if the given message is valid, meets requirements
 *      errorMessages:      an array of strings containing information on failed checks;
 *                              empty if the string passes validation
 */
export const validateCustomSubscriptionConfirmationMessage = (message) => {
    if (typeof message !== "string") {
        throw new Error("Message to validate must be a string")
    }

    let isValid = true
    const errorMessages = []

    // verify message length is not more than 1 SMS message
    if (message && message.length > MAX_CHARS_PER_TEXT) {
        isValid = false
        errorMessages.push(
            `Your subscription confirmation message must be ${MAX_CHARS_PER_TEXT} characters or shorter.`,
        )
    }

    // check that the disclaimer is included
    if (!containsDisclaimerString(message)) {
        isValid = false
        errorMessages.push("Your subscription message must contain the required disclaimer text.")
    }

    return {
        isValid,
        errorMessages,
    }
}
